import {
  Box,
  BoxProps,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  Flex,
  Heading,
  HeadingProps,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  TextProps,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";

import { startCase } from "lodash";
import { ReactNode } from "react";
import QRCode from "react-qr-code";
import LabelText from "../LabelText";

export interface DetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  drawerBodyHead?: ReactNode;
  labeledListItem?: Record<string, string | number | ReactNode | any>;
  actionButtons?: ReactNode | Iterable<ReactNode>;
  children?: ReactNode;
  showListBodyItems?: boolean;
  movieDetailDrawerLabelTextProps?: TextProps;
  drawerContentProps?: DrawerContentProps;
  drawerHeadingTextProps?: HeadingProps;
  ticketCodeLabelProps?: TextProps;
  ticketCodeTextProps?: TextProps;
  // Order section theming
  orderCardProps?: BoxProps;
  orderBadgeProps?: BoxProps;
  orderBadgeTextProps?: TextProps;
  orderTableContainerProps?: BoxProps;
  orderTableHeadProps?: Record<string, unknown>;
  orderTableThProps?: Record<string, unknown>;
  orderTableRowProps?: Record<string, unknown>;
  orderTableCellProps?: Record<string, unknown>;
  orderSummaryBoxProps?: BoxProps;
  orderSummaryLabelProps?: TextProps;
  orderSummaryValueProps?: TextProps;
  orderTotalAmountProps?: TextProps;
}

export function DetailDrawer({
  isOpen,
  onClose,
  title,
  children,
  labeledListItem,
  showListBodyItems,
  drawerBodyHead,
  actionButtons,
  movieDetailDrawerLabelTextProps,
  drawerContentProps,
  drawerHeadingTextProps,
  ticketCodeLabelProps,
  ticketCodeTextProps,
  orderCardProps,
  orderBadgeProps,
  orderBadgeTextProps,
  orderTableContainerProps,
  orderTableHeadProps,
  orderTableThProps,
  orderTableRowProps,
  orderTableCellProps,
  orderSummaryBoxProps,
  orderSummaryLabelProps,
  orderSummaryValueProps,
  orderTotalAmountProps,
  ...props
}: DetailDrawerProps & Omit<DrawerProps, "isOpen" | "onClose" | "children">) {
  return (
    <Drawer isOpen={isOpen} onClose={onClose} {...props}>
      <DrawerOverlay />
      <DrawerContent {...drawerContentProps}>
        <DrawerHeader {...drawerHeadingTextProps}>
          <Heading size="lg" mb={0}>
            {title}
          </Heading>
          <DrawerCloseButton />
        </DrawerHeader>

        <DrawerBody>
          <>{drawerBodyHead}</>
          {showListBodyItems && (
            <Box
              sx={{
                my: "1rem",
              }}
            >
              {labeledListItem &&
                Object.keys(labeledListItem)
                  ?.filter((fkey) => fkey !== "ticket_code" && fkey !== "order")
                  .map((key) => (
                    <LabelText
                      key={key}
                      labelProps={{
                        fontWeight: "normal",
                        flexBasis: "35%",
                        color: "surface.800 !important",
                      }}
                      textProps={{
                        fontWeight: 600,
                        fontSize: "1rem",
                        color: "surface.500 !important",
                      }}
                      label={startCase(key)}
                      text={labeledListItem[key] as string}
                      {...movieDetailDrawerLabelTextProps}
                    />
                  ))}
              {labeledListItem?.["ticket_code"] && (
                <Flex
                  fontWeight="bold"
                  color="surface.700"
                  w="full"
                  {...props}
                  gap="1rem"
                  sx={{
                    textTransform: "capitalize",
                  }}
                >
                  <Text
                    fontWeight="normal"
                    color="surface.800"
                    flexBasis="35%"
                    {...ticketCodeLabelProps}
                  >
                    Ticket Code :
                  </Text>
                  <Box>
                    <Text
                      mb={2}
                      textAlign={"center"}
                      color={"surface.500"}
                      {...ticketCodeTextProps}
                    >
                      {labeledListItem?.["ticket_code"] as string}
                    </Text>
                    <Box p={1} bg={"white"}>
                      <QRCode
                        value={labeledListItem?.["ticket_code"] as string}
                        size={150}
                      />
                    </Box>
                  </Box>
                </Flex>
              )}
              <Divider my={3} />
              <Box mt={4}>
                {Array.isArray(labeledListItem?.["order"]) &&
                  labeledListItem?.["order"]?.map(
                    (item: any, index: number) => (
                      <Box
                        key={item.idx}
                        w="full"
                        display={"flex"}
                        flexDirection="column"
                        mb={4}
                        p={3}
                        bg="surface.50"
                        border="1px solid"
                        borderColor="surface.100"
                        borderRadius="lg"
                        {...orderCardProps}
                      >
                        <Flex
                          justify="center"
                          align="center"
                          bg="surface.100"
                          py={1}
                          px={3}
                          borderRadius="md"
                          mb={3}
                          {...orderBadgeProps}
                        >
                          <Text
                            fontWeight="bold"
                            color="surface.800"
                            fontSize="xs"
                            letterSpacing="wider"
                            textTransform="uppercase"
                            {...orderBadgeTextProps}
                          >
                            ORDER #{index + 1}
                          </Text>
                        </Flex>

                        <TableContainer
                          mb={3}
                          bg="white"
                          border="1px solid"
                          borderColor="surface.100"
                          borderRadius="md"
                          {...orderTableContainerProps}
                        >
                          <Table variant="simple" size="sm">
                            <Thead bg="surface.50" {...orderTableHeadProps}>
                              <Tr>
                                <Th
                                  color="surface.500"
                                  fontSize="xs"
                                  textTransform="uppercase"
                                  letterSpacing="wider"
                                  py={2}
                                  {...orderTableThProps}
                                >
                                  Item Name
                                </Th>
                                <Th
                                  isNumeric
                                  color="surface.500"
                                  fontSize="xs"
                                  textTransform="uppercase"
                                  letterSpacing="wider"
                                  py={2}
                                  {...orderTableThProps}
                                >
                                  Qty
                                </Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {item.food_menu?.map(
                                ({ quantity, name }: any) => (
                                  <Tr
                                    key={name}
                                    _hover={{ bg: "surface.25" }}
                                    {...orderTableRowProps}
                                  >
                                    <Td
                                      color="surface.800"
                                      py={3}
                                      fontSize="sm"
                                      fontWeight="medium"
                                      {...orderTableCellProps}
                                    >
                                      {name}
                                    </Td>
                                    <Td
                                      isNumeric
                                      color="surface.900"
                                      py={3}
                                      fontSize="sm"
                                      fontWeight="bold"
                                      {...orderTableCellProps}
                                    >
                                      {quantity}
                                    </Td>
                                  </Tr>
                                ),
                              )}
                            </Tbody>
                          </Table>
                        </TableContainer>

                        <Box
                          bg="white"
                          p={3}
                          borderRadius="md"
                          border="1px solid"
                          borderColor="surface.50"
                          {...orderSummaryBoxProps}
                        >
                          <Flex justify="space-between" mb={2}>
                            <Text
                              fontSize="sm"
                              color="surface.600"
                              {...orderSummaryLabelProps}
                            >
                              Method:
                            </Text>
                            <Text
                              fontSize="sm"
                              fontWeight="bold"
                              color="surface.800"
                              textTransform="uppercase"
                              {...orderSummaryValueProps}
                            >
                              {item.payment_method}
                            </Text>
                          </Flex>
                          <Flex justify="space-between" mb={2}>
                            <Text
                              fontSize="sm"
                              color="surface.600"
                              {...orderSummaryLabelProps}
                            >
                              Total:
                            </Text>
                            <Text
                              fontSize="sm"
                              fontWeight="bold"
                              color="blue.600"
                              {...orderTotalAmountProps}
                            >
                              Rs. {item.amount}
                            </Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text
                              fontSize="sm"
                              color="surface.600"
                              {...orderSummaryLabelProps}
                            >
                              Date:
                            </Text>
                            <Text
                              fontSize="sm"
                              color="surface.500"
                              {...orderSummaryValueProps}
                            >
                              {new Date(item.payment_date).toLocaleString([], {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })}
                            </Text>
                          </Flex>
                        </Box>
                      </Box>
                    ),
                  )}
              </Box>
            </Box>
          )}
          <>{children}</>
        </DrawerBody>
        <DrawerFooter>
          <>{actionButtons}</>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
